import { useState, useMemo } from "react";
import styles from "./AddBooking.module.css";
import ssrStyles from "./StepSSR.module.css";
import { useGetSSRCategories, useGetSSRItems } from "../../api/products/useSSRApi";
import { SSR_STATUS_VALUES, ssrStatusColor } from "../../constants/documentConstants";

export const SSR_CATEGORIES = [
  { id: 1, code: "SEAT",    name: "Seat",            icon: "💺", description: "Seat selection and preferences",    color: "#3b82f6", bg: "#eff6ff" },
  { id: 2, code: "MEAL",    name: "Meal",            icon: "🍽️", description: "Special meal requests",             color: "#10b981", bg: "#ecfdf5" },
  { id: 3, code: "HANDBAG", name: "Hand Baggage",    icon: "👜", description: "Cabin baggage allowance",           color: "#8b5cf6", bg: "#f5f3ff" },
  { id: 4, code: "BAGGAGE", name: "Baggage",         icon: "🧳", description: "Checked baggage allowance",         color: "#f59e0b", bg: "#fffbeb" },
  { id: 5, code: "ASSIST",  name: "Assistance",      icon: "♿", description: "Special assistance services",       color: "#ef4444", bg: "#fef2f2" },
  { id: 6, code: "SPORT",   name: "Sport Equipment", icon: "⛷️", description: "Sports and special equipment",      color: "#06b6d4", bg: "#ecfeff" },
  { id: 7, code: "PET",     name: "Pet",             icon: "🐕", description: "Pet travel services",              color: "#ec4899", bg: "#fdf2f8" },
  { id: 8, code: "OTHER",   name: "Other",           icon: "📋", description: "Other special services",           color: "#6b7280", bg: "#f9fafb" },
];

/* Local style map for categories — bg color / icon fallbacks layered over API data */
const CATEGORY_STYLE_MAP = SSR_CATEGORIES.reduce((acc, c) => {
  acc[c.code] = { color: c.color, bg: c.bg, icon: c.icon };
  return acc;
}, {});

const findCatIn = (list, code) => (list || []).find((c) => c.code === code);
const findCat = (code) => SSR_CATEGORIES.find((c) => c.code === code);

/* Derive a UI unit label from an API SSR item's flags */
const deriveUnit = (apiItem) => {
  if (apiItem.travelerRequired && apiItem.flightSegmentRequired) return "per person/segment";
  if (apiItem.flightSegmentRequired) return "per segment";
  if (apiItem.travelerRequired) return "per person";
  return "per item";
};

/* Combine maxWeight/maxSize/maxPieces into a friendly description suffix */
const deriveDescSuffix = (apiItem) => {
  const parts = [];
  if (apiItem.maxWeight != null) parts.push(`Max ${apiItem.maxWeight} kg`);
  if (apiItem.maxSize) parts.push(`Max size ${apiItem.maxSize}`);
  if (apiItem.maxPieces != null) parts.push(`Max ${apiItem.maxPieces} pcs`);
  return parts.join(" • ");
};

/* ─── Icons ─── */
const PlaneIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
  </svg>
);
const UserIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const RouteIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="19" r="3" /><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" /><circle cx="18" cy="5" r="3" />
  </svg>
);
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const XIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
    <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M6.5 2v9M2 6.5h9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const ArrowLeftIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M8 2.5L4 6.5l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M10 10l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

/* ════════════════════════════════════════════════════════════════════════
   CONTEXT SELECTOR — Flight → Leg → Traveler
   ════════════════════════════════════════════════════════════════════════ */

const ContextSelector = ({
  flights, travelers, products,
  selectedFlightId, selectedLegId, selectedTravelerId,
  onSelectFlight, onSelectLeg, onSelectTraveler,
  ssrCountByContext,
}) => {
  const hasFlights = flights.length > 0;
  const hasTravelers = travelers.length > 0;
  const selectedFlight = flights.find((f) => f.id === selectedFlightId);
  const legs = selectedFlight?.legs || [];

  const dirLabel = (dir) => {
    if (!dir) return "";
    if (dir === "Outbound") return "OUT";
    if (dir === "Inbound") return "INB";
    return dir.substring(0, 3).toUpperCase();
  };

  return (
    <div className={ssrStyles.ctxPanel}>
      {/* ── Flight Selection ── */}
      <div className={ssrStyles.ctxSection}>
        <div className={ssrStyles.ctxSectionHeader}>
          <span className={ssrStyles.ctxSectionIcon}><PlaneIcon /></span>
          <span className={ssrStyles.ctxSectionTitle}>Flight</span>
          {!hasFlights && <span className={ssrStyles.ctxNone}>No flights in booking</span>}
        </div>
        {hasFlights && (
          <div className={ssrStyles.ctxChipRow}>
            {flights.map((fl) => {
              const firstLeg = (fl.legs || [])[0] || {};
              const lastLeg = (fl.legs || [])[(fl.legs || []).length - 1] || firstLeg;
              const route = firstLeg.departure && lastLeg.arrival
                ? `${firstLeg.departure} → ${lastLeg.arrival}`
                : "No route";
              const isActive = selectedFlightId === fl.id;
              const count = Object.keys(ssrCountByContext).filter(k => k.startsWith(`${fl.id}|`)).reduce((s, k) => s + ssrCountByContext[k], 0);
              return (
                <button
                  key={fl.id}
                  className={`${ssrStyles.ctxFlightCard} ${isActive ? ssrStyles.ctxFlightCardActive : ""}`}
                  onClick={() => onSelectFlight(fl.id)}
                >
                  <div className={ssrStyles.ctxFlightRoute}>
                    <PlaneIcon /> {route}
                  </div>
                  <div className={ssrStyles.ctxFlightMeta}>
                    {fl.supplier && <span>{fl.supplier}</span>}
                    {firstLeg.date && <span>{firstLeg.date}</span>}
                    {fl.legs && <span>{fl.legs.length} leg{fl.legs.length !== 1 ? "s" : ""}</span>}
                  </div>
                  {count > 0 && <span className={ssrStyles.ctxBadge}>{count} SSR{count !== 1 ? "s" : ""}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Leg / Segment Selection ── */}
      {selectedFlight && legs.length > 0 && (
        <div className={ssrStyles.ctxSection}>
          <div className={ssrStyles.ctxSectionHeader}>
            <span className={ssrStyles.ctxSectionIcon}><RouteIcon /></span>
            <span className={ssrStyles.ctxSectionTitle}>Segment / Leg</span>
            <span className={ssrStyles.ctxHint}>Select which leg this SSR applies to</span>
          </div>
          <div className={ssrStyles.ctxChipRow}>
            <button
              className={`${ssrStyles.ctxLegChip} ${selectedLegId === "__all__" ? ssrStyles.ctxLegChipActive : ""}`}
              onClick={() => onSelectLeg("__all__")}
            >
              <span className={ssrStyles.ctxLegDir}>ALL</span>
              <span className={ssrStyles.ctxLegRoute}>All Segments</span>
            </button>
            {legs.map((leg, idx) => {
              const isActive = selectedLegId === leg.id;
              const route = leg.departure && leg.arrival ? `${leg.departure} → ${leg.arrival}` : `Leg ${idx + 1}`;
              const dir = dirLabel(leg.direction);
              const contextKey = `${selectedFlightId}|${leg.id}`;
              const count = ssrCountByContext[contextKey] || 0;
              return (
                <button
                  key={leg.id}
                  className={`${ssrStyles.ctxLegChip} ${isActive ? ssrStyles.ctxLegChipActive : ""}`}
                  onClick={() => onSelectLeg(leg.id)}
                >
                  {dir && <span className={ssrStyles.ctxLegDir}>{dir}</span>}
                  <span className={ssrStyles.ctxLegRoute}>{route}</span>
                  {leg.airlineCode && leg.flightNumber && (
                    <span className={ssrStyles.ctxLegFlight}>{leg.airlineCode}{leg.flightNumber}</span>
                  )}
                  {leg.date && <span className={ssrStyles.ctxLegDate}>{leg.date}</span>}
                  {count > 0 && <span className={ssrStyles.ctxLegBadge}>{count}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Traveler Selection ── */}
      {hasTravelers && (
        <div className={ssrStyles.ctxSection}>
          <div className={ssrStyles.ctxSectionHeader}>
            <span className={ssrStyles.ctxSectionIcon}><UserIcon /></span>
            <span className={ssrStyles.ctxSectionTitle}>Traveler</span>
            <span className={ssrStyles.ctxHint}>SSRs will be linked to the selected traveler</span>
          </div>
          <div className={ssrStyles.ctxChipRow}>
            <button
              className={`${ssrStyles.ctxTravChip} ${selectedTravelerId === "__all__" ? ssrStyles.ctxTravChipActive : ""}`}
              onClick={() => onSelectTraveler("__all__")}
            >
              <span className={ssrStyles.ctxTravAvatar}>All</span>
              <span>All Travelers</span>
            </button>
            {travelers.map((t, i) => {
              const name = `${t.firstName || ""} ${(t.lastName || "").toUpperCase()}`.trim() || `Traveler ${i + 1}`;
              const initials = ((t.firstName?.[0] || "") + (t.lastName?.[0] || "")).toUpperCase();
              const isActive = selectedTravelerId === String(i);
              const genderIcon = t.gender === "Male" ? "♂" : t.gender === "Female" ? "♀" : "";
              return (
                <button
                  key={i}
                  className={`${ssrStyles.ctxTravChip} ${isActive ? ssrStyles.ctxTravChipActive : ""}`}
                  onClick={() => onSelectTraveler(String(i))}
                >
                  <span className={ssrStyles.ctxTravAvatar}>{initials || (i + 1)}</span>
                  <span className={ssrStyles.ctxTravName}>{name}</span>
                  {genderIcon && <span className={ssrStyles.ctxTravGender}>{genderIcon}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   CATEGORY CARD
   ════════════════════════════════════════════════════════════════════════ */

const CategoryCard = ({ cat, count, onClick }) => (
  <button
    className={ssrStyles.catCard}
    onClick={onClick}
    style={{ "--cat-color": cat.color, "--cat-bg": cat.bg }}
  >
    <span className={ssrStyles.catIcon}>{cat.icon}</span>
    <div className={ssrStyles.catInfo}>
      <span className={ssrStyles.catLabel}>{cat.name}</span>
      <span className={ssrStyles.catDesc}>{cat.description}</span>
    </div>
    {count > 0 && (
      <span className={ssrStyles.catBadge} style={{ background: cat.color }}>
        {count}
      </span>
    )}
  </button>
);

/* ════════════════════════════════════════════════════════════════════════
   SSR ITEM ROW
   ════════════════════════════════════════════════════════════════════════ */

const SSRItemRow = ({ item, isSelected, existing, onToggle, onQtyChange }) => {
  const cat = findCat(item.category);

  return (
    <div className={`${ssrStyles.itemRow} ${isSelected ? ssrStyles.itemRowSelected : ""}`}>
      <button
        className={`${ssrStyles.itemCheck} ${isSelected ? ssrStyles.itemCheckActive : ""}`}
        style={isSelected ? { background: cat?.color, borderColor: cat?.color, color: 'white' } : {}}
        onClick={() => onToggle(item)}
        aria-label={isSelected ? "Remove" : "Add"}
      >
        {isSelected && <CheckIcon />}
      </button>

      <div
        className={ssrStyles.itemCodeBadge}
        style={isSelected ? { background: cat?.bg, color: cat?.color } : {}}
      >
        {item.code}
      </div>

      <div className={ssrStyles.itemBody}>
        <div className={ssrStyles.itemName}>{item.label}</div>
        <div className={ssrStyles.itemDesc}>{item.desc}</div>
      </div>

      <div className={ssrStyles.itemUnit}>{item.unit}</div>

      {isSelected && existing && (
        <div className={ssrStyles.itemControls}>
          <div className={ssrStyles.qtyControl}>
            <button className={ssrStyles.qtyBtn} onClick={() => onQtyChange(item.id, Math.max(1, (existing.qty || 1) - 1))}>−</button>
            <span className={ssrStyles.qtyVal}>{existing.qty || 1}</span>
            <button className={ssrStyles.qtyBtn} onClick={() => onQtyChange(item.id, (existing.qty || 1) + 1)}>+</button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   ADD CUSTOM SSR MODAL
   ════════════════════════════════════════════════════════════════════════ */

const AddCustomSSRModal = ({ onClose, onAdd, categories = SSR_CATEGORIES }) => {
  const [form, setForm] = useState({
    code: "", label: "", desc: "", category: "OTHER",
    unit: "per person", qty: 1, notes: "",
  });
  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));
  const canAdd = form.label.trim() && form.code.trim();

  const handleAdd = () => {
    if (!canAdd) return;
    const ts = Date.now();
    onAdd({
      id: `custom_${ts}`,
      itemId: `custom_${ts}`,
      code: form.code.toUpperCase(),
      label: form.label,
      desc: form.desc,
      category: form.category,
      unit: form.unit,
      qty: form.qty,
      notes: form.notes,
      isCustom: true,
      status: "Requested",
    });
    onClose();
  };

  return (
    <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal} style={{ maxWidth: 560 }}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <span style={{ fontSize: 20 }}>📋</span>
            <h3 className={styles.modalTitle}>Add Custom SSR</h3>
          </div>
          <button className={styles.modalClose} onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.modalFormLayout}>
            <div className={styles.mfSection}>
              <div className={styles.mfRow2}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>SSR Code <span className={styles.req}>*</span></label>
                  <input
                    className={styles.input}
                    placeholder="e.g. SPRMQ"
                    value={form.code}
                    onChange={(e) => set("code", e.target.value.toUpperCase())}
                    maxLength={10}
                    style={{ fontFamily: "monospace", textTransform: "uppercase" }}
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Category</label>
                  <select className={styles.input} value={form.category} onChange={(e) => set("category", e.target.value)}>
                    {categories.map((c) => (
                      <option key={c.code} value={c.code}>{c.icon} {c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Label / Name <span className={styles.req}>*</span></label>
                <input className={styles.input} placeholder="e.g. Special Meal Request" value={form.label} onChange={(e) => set("label", e.target.value)} />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Description</label>
                <input className={styles.input} placeholder="Short description of the service" value={form.desc} onChange={(e) => set("desc", e.target.value)} />
              </div>
            </div>

            <div className={styles.mfSection}>
              <div className={styles.mfRow2}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Unit</label>
                  <select className={styles.input} value={form.unit} onChange={(e) => set("unit", e.target.value)}>
                    {["per person", "per segment", "per trip", "per night", "per item", "per bag", "per stay", "per flight", "flat rate"].map((u) => (
                      <option key={u}>{u}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Quantity</label>
                  <input className={styles.input} type="number" min="1" max="99" value={form.qty} onChange={(e) => set("qty", parseInt(e.target.value) || 1)} />
                </div>
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Internal Notes</label>
                <textarea className={`${styles.input} ${styles.textarea}`} rows={2} placeholder="Any notes for the operations team..." value={form.notes} onChange={(e) => set("notes", e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose}>Cancel</button>
          <button className={`${styles.btnPrimary} ${!canAdd ? styles.btnDisabled : ""}`} disabled={!canAdd} onClick={handleAdd}>
            <PlusIcon /> Add SSR Item
          </button>
        </div>
      </div>
    </div>
  );
};


/* ════════════════════════════════════════════════════════════════════════
   MAIN STEP SSR COMPONENT
   ════════════════════════════════════════════════════════════════════════ */

const StepSSR = ({ data, onChange, onRemoveSSR, onPersistSSRStatus }) => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddCustom, setShowAddCustom] = useState(false);

  /* ── Fetch SSR catalog from backend (categories + items) ── */
  const { data: apiCategories, loading: loadingCategories } = useGetSSRCategories();
  const { data: apiItems, loading: loadingItems } = useGetSSRItems({ status: "active", limit: 500 });

  /* Merge API categories with local style map (color/bg/icon fallback) */
  const categoryList = useMemo(() => {
    const source = (apiCategories && apiCategories.length > 0) ? apiCategories : SSR_CATEGORIES;
    return source.map((c) => {
      const style = CATEGORY_STYLE_MAP[c.code] || {};
      return {
        id: c.id,
        code: c.code,
        name: c.name,
        description: c.description,
        icon: c.icon || style.icon || "📋",
        color: c.color || style.color || "#6b7280",
        bg: style.bg || "#f9fafb",
      };
    });
  }, [apiCategories]);

  /* Map API items into the shape the catalog UI expects */
  const ssrCatalog = useMemo(() => {
    return (apiItems || []).map((it) => {
      const suffix = deriveDescSuffix(it);
      return {
        id: it.id,
        code: it.code,
        label: it.description || it.code,
        desc: suffix || it.description || "",
        category: it.category,
        unit: deriveUnit(it),
      };
    });
  }, [apiItems]);

  const findCatLocal = (code) => findCatIn(categoryList, code) || findCat(code);

  // Context selections — seeded from data.ssrContext so they survive step navigation
  const ssrContext = data.ssrContext || {};
  const [selectedFlightId, setSelectedFlightId] = useState(ssrContext.selectedFlightId ?? "");
  // Segment starts UNSELECTED — the user must explicitly pick All Segments or a
  // specific leg before the SSR category catalog appears.
  const [selectedLegId, setSelectedLegId] = useState(ssrContext.selectedLegId ?? "");
  const [selectedTravelerId, setSelectedTravelerId] = useState(ssrContext.selectedTravelerId ?? "__all__");

  // Persist a context field back to data so coming back restores the selection
  const persistContext = (patch) => {
    onChange("ssrContext", { ...ssrContext, ...patch });
  };

  const ssrItems = data.ssrItems || [];
  const travelers = data.travelers || [];
  const products = data.products || [];
  const flights = products.filter((p) => p.type === "Flight");

  // Auto-select single flight (also persist if auto-selected)
  const activeFlightId = useMemo(() => {
    if (flights.length === 1 && !selectedFlightId) return flights[0].id;
    return selectedFlightId;
  }, [flights, selectedFlightId]);

  // When flight changes, clear the segment so the user re-picks it (nothing
  // pre-selected) before categories show for the new flight.
  const handleSelectFlight = (id) => {
    setSelectedFlightId(id);
    setSelectedLegId("");
    persistContext({ selectedFlightId: id, selectedLegId: "" });
  };

  // Segment stored on each SSR row. A flight with no legs leaves selectedLegId
  // empty; persist it as "__all__" so downstream save logic is unchanged.
  const effLegId = selectedLegId || "__all__";

  // Build SSR count map by context: "flightId|legId" => count
  const ssrCountByContext = useMemo(() => {
    const map = {};
    ssrItems.forEach((s) => {
      const key = `${s.flightProductId || ""}|${s.legId || ""}`;
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [ssrItems]);

  const countByCategory = useMemo(() => {
    const map = {};
    ssrItems.forEach((s) => { map[s.category] = (map[s.category] || 0) + 1; });
    return map;
  }, [ssrItems]);

  const totalSelected = ssrItems.length;

  const filteredItems = useMemo(() => {
    let items = ssrCatalog;
    if (activeCategory) items = items.filter((i) => i.category === activeCategory);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (i) => (i.label || "").toLowerCase().includes(q) || (i.code || "").toLowerCase().includes(q) || (i.desc || "").toLowerCase().includes(q)
      );
    }
    return items;
  }, [activeCategory, searchQuery, ssrCatalog]);

  const activeCat = findCatLocal(activeCategory);

  // Helper to get traveler label
  const getTravelerLabel = (travelerIndex) => {
    const t = travelers[travelerIndex];
    console.log("Getting label for traveler index", travelerIndex, "->", t);
    return t ? `${t.firstName || ""} ${(t.lastName || "").toUpperCase()}`.trim() || `Traveler ${travelerIndex + 1}` : `Traveler ${travelerIndex + 1}`;
  };
  const getTravelerLabelNew = (travelerIndex) => {
    const t = travelers[travelerIndex];
    console.log("Getting information for traveler indexxxxxx", travelerIndex, "->", t);
    return t ? t : null;
  };

//   const getTravelerLabel = (travelerIndex) => {
//     const t = travelers[travelerIndex];
// ;
//   };

  // Helper to get leg label
  const getLegLabel = (flightId, legId) => {
    if (legId === "__all__") return "All Segments";
    const fl = flights.find((f) => f.id === flightId);
    const leg = (fl?.legs || []).find((l) => l.id === legId);
    return leg ? (leg.departure && leg.arrival ? `${leg.departure} → ${leg.arrival}` : legId) : legId;
  };

  // Does an SSR row match the current context? When "All Travelers" is selected
  // the SSR is stored per-traveler (travelerId "0","1",…), so we match on
  // item/flight/leg only and ignore travelerId.
  const matchesContext = (s, itemId) =>
    s.itemId === itemId &&
    s.flightProductId === activeFlightId &&
    s.legId === selectedLegId &&
    (selectedTravelerId === "__all__" || s.travelerId === selectedTravelerId);

  // All SSR rows for this item in the current context (one per traveler in __all__ mode)
  const findMatching = (itemId) => ssrItems.filter((s) => matchesContext(s, itemId));
  // First matching row — drives the checkbox "selected" state and qty display
  const findExisting = (itemId) => findMatching(itemId)[0];

  // Toggle SSR item - handles "All Travelers" (multiple rows) properly
  const toggleItem = (item) => {
    const matches = findMatching(item.id);

    if (matches.length > 0) {
      // Already selected → remove every matching row (all travelers in __all__ mode)
      const serverBacked = matches.filter((m) => m._serverId);
      const localOnly = matches.filter((m) => !m._serverId);
      if (serverBacked.length > 0 && onRemoveSSR) serverBacked.forEach((m) => onRemoveSSR(m));
      if (localOnly.length > 0) onChange("ssrItems", ssrItems.filter((s) => !localOnly.includes(s)));
    } else {
      // Add SSR - if "All Travelers" is selected, create one SSR per traveler
      const newItems = [];

      if (selectedTravelerId === "__all__") {
        // Create an SSR entry for each traveler
        travelers.forEach((t, travelerIndex) => {
          newItems.push({
            itemId: item.id,
            code: item.code,
            label: item.label,
            desc: item.desc,
            category: item.category,
            unit: item.unit,
            qty: 1,
            travelerId: String(travelerIndex),
            travelerLabel: getTravelerLabel(travelerIndex),
            travelerInformation: getTravelerLabelNew(travelerIndex),
            flightProductId: activeFlightId,
            legId: effLegId,
            legLabel: getLegLabel(activeFlightId, effLegId),
            notes: "",
            status: "Requested",
          });
        });
      } else {
        // Create a single SSR for the selected traveler
        newItems.push({
          itemId: item.id,
          code: item.code,
          label: item.label,
          desc: item.desc,
          category: item.category,
          unit: item.unit,
          qty: 1,
          travelerId: selectedTravelerId,
          travelerLabel: getTravelerLabel(parseInt(selectedTravelerId)),
         travelerInformation: getTravelerLabelNew(parseInt(selectedTravelerId)),
          flightProductId: activeFlightId,
          legId: effLegId,
          legLabel: getLegLabel(activeFlightId, effLegId),
          notes: "",
          status: "Requested",
        });
      }
      
      onChange("ssrItems", [...ssrItems, ...newItems]);
    }
  };

  const updateQty = (itemId, qty) =>
    onChange("ssrItems", ssrItems.map((s) =>
      matchesContext(s, itemId) ? { ...s, qty } : s
    ));

  // Status is per individual SSR row (per traveler), so update by index.
  // For rows already saved to the server (_serverId), persist the change
  // immediately so a later edit on a returned-to Step 5 isn't silently dropped.
  const updateStatus = (idx, status) => {
    const target = ssrItems[idx];
    if (target?.status === status) return;
    onChange("ssrItems", ssrItems.map((s, i) => (i === idx ? { ...s, status } : s)));
    if (target?._serverId && onPersistSSRStatus) onPersistSSRStatus(target._serverId, status);
  };

  const removeItem = (index) => {
    const target = ssrItems[index];
    if (target?._serverId && onRemoveSSR) {
      onRemoveSSR(target);
    } else {
      onChange("ssrItems", ssrItems.filter((_, i) => i !== index));
    }
  };

  const addCustom = (item) => {
    const newItems = [];
    
    if (selectedTravelerId === "__all__") {
      // Create an entry for each traveler
      travelers.forEach((t, travelerIndex) => {
        newItems.push({
          ...item,
          travelerId: String(travelerIndex),
          travelerLabel: getTravelerLabel(travelerIndex),
          travelerInformation: getTravelerLabelNew(travelerIndex),
          flightProductId: activeFlightId,
          legId: effLegId,
          legLabel: getLegLabel(activeFlightId, effLegId),
        });
      });
    } else {
      // Create a single entry
      newItems.push({
        ...item,
        travelerId: selectedTravelerId,
        travelerLabel: getTravelerLabel(parseInt(selectedTravelerId)),
        travelerInformation: getTravelerLabelNew(parseInt(selectedTravelerId)),
        flightProductId: activeFlightId,
        legId: effLegId,
        legLabel: getLegLabel(activeFlightId, effLegId),
      });
    }
    
    onChange("ssrItems", [...ssrItems, ...newItems]);
  };

  const needsFlightSelection = flights.length > 0 && !activeFlightId;
  // Once a flight is chosen, the user must pick a segment (unless the flight has
  // no legs) before the category catalog appears.
  const activeFlightLegs = (flights.find((f) => f.id === activeFlightId)?.legs) || [];
  const needsSegmentSelection = !!activeFlightId && activeFlightLegs.length > 0 && !selectedLegId;

  const getFlightLabel = (flightId) => {
    const fl = flights.find((f) => f.id === flightId);
    if (!fl) return "";
    const firstLeg = (fl.legs || [])[0] || {};
    return firstLeg.departure && firstLeg.arrival ? `${firstLeg.departure} → ${firstLeg.arrival}` : fl.supplier || fl.id;
  };

  // Active context summary — the catalog is gated on a chosen segment, not just
  // a chosen flight (a flight with no legs is ready immediately).
  const contextReady =
    flights.length === 0 ||
    (!!activeFlightId && (activeFlightLegs.length === 0 || !!selectedLegId));

  return (
    <div className={styles.stepContent}>

      {/* ── Top bar ── */}
      <div className={ssrStyles.topBar}>
        <div className={ssrStyles.summaryPills}>
          <div className={ssrStyles.summaryPill}>
            <span className={ssrStyles.pillNum}>{totalSelected}</span>
            <span className={ssrStyles.pillLabel}>services selected</span>
          </div>
        </div>
        <div className={ssrStyles.topBarActions}>
          <div className={styles.searchWrap} style={{ width: 260 }}>
            <span className={styles.searchSvg}><SearchIcon /></span>
            <input
              className={styles.searchField}
              placeholder="Search SSR items..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value) setActiveCategory(null);
              }}
            />
            {searchQuery && (
              <button className={styles.searchClearBtn} onClick={() => setSearchQuery("")}><XIcon /></button>
            )}
          </div>
          <button className={styles.addBtn} onClick={() => setShowAddCustom(true)}>
            <PlusIcon /> Custom SSR
          </button>
        </div>
      </div>

      {/* ── Context Selector (Flight / Leg / Traveler) ── */}
      <ContextSelector
        flights={flights}
        travelers={travelers}
        products={products}
        selectedFlightId={activeFlightId}
        selectedLegId={selectedLegId}
        selectedTravelerId={selectedTravelerId}
        onSelectFlight={handleSelectFlight}
        onSelectLeg={(id) => { setSelectedLegId(id); persistContext({ selectedLegId: id }); }}
        onSelectTraveler={(id) => { setSelectedTravelerId(id); persistContext({ selectedTravelerId: id }); }}
        ssrCountByContext={ssrCountByContext}
      />

      {/* ── Active context summary chip bar ── */}
      {contextReady && (
        <div className={ssrStyles.activeCtxBar}>
          <span className={ssrStyles.activeCtxLabel}>Adding SSR for:</span>
          {activeFlightId && (
            <span className={ssrStyles.activeCtxChip}>
              <PlaneIcon /> {getFlightLabel(activeFlightId)}
            </span>
          )}
          {selectedLegId !== "__all__" && (
            <span className={ssrStyles.activeCtxChip}>
              <RouteIcon /> {getLegLabel(activeFlightId, selectedLegId)}
            </span>
          )}
          {selectedLegId === "__all__" && activeFlightId && (
            <span className={ssrStyles.activeCtxChipMuted}>All Segments</span>
          )}
          {selectedTravelerId !== "__all__" ? (
            <span className={ssrStyles.activeCtxChip}>
              <UserIcon /> {getTravelerLabel(parseInt(selectedTravelerId))}
            </span>
          ) : (
            <span className={ssrStyles.activeCtxChipMuted}>All Travelers</span>
          )}
        </div>
      )}

      {/* ── Needs flight selection prompt ── */}
      {needsFlightSelection && (
        <div className={styles.infoBox} style={{ textAlign: "center", padding: "20px" }}>
          <strong>Select a flight product above</strong> to start adding SSR items. Each SSR must be linked to a specific flight.
        </div>
      )}

      {/* ── Needs segment selection prompt ── */}
      {needsSegmentSelection && (
        <div className={styles.infoBox} style={{ textAlign: "center", padding: "20px" }}>
          <strong>Select a segment above</strong> — choose <em>All Segments</em> or a specific leg to see the SSR categories.
        </div>
      )}

      {/* ── SSR Catalog ── */}
      {contextReady && (
        <>
          {searchQuery ? (
            <div>
              <div className={ssrStyles.sectionHeading}>
                Search results for "{searchQuery}" — {filteredItems.length} item{filteredItems.length !== 1 ? "s" : ""}
                {(loadingItems || loadingCategories) && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: "var(--text-tertiary)" }}>Loading…</span>
                )}
              </div>
              {filteredItems.length === 0 ? (
                <div className={styles.infoBox}>No SSR items found. Try adding a <strong>Custom SSR</strong> above.</div>
              ) : (
                <div className={ssrStyles.itemList}>
                  {filteredItems.map((item) => {
                    const existing = findExisting(item.id);
                    return (
                      <SSRItemRow
                        key={item.id} item={item}
                        isSelected={!!existing} existing={existing}
                        onToggle={toggleItem} onQtyChange={updateQty}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          ) : activeCategory ? (
            <div>
              <div className={ssrStyles.categoryBreadcrumb}>
                <button className={ssrStyles.breadcrumbBack} onClick={() => setActiveCategory(null)}>
                  <ArrowLeftIcon /> All Categories
                </button>
                <span className={ssrStyles.breadcrumbSep}>/</span>
                <span className={ssrStyles.breadcrumbCurrent} style={{ color: activeCat?.color }}>
                  {activeCat?.icon} {activeCat?.name}
                </span>
              </div>
              {filteredItems.length === 0 ? (
                <div className={styles.infoBox}>No items in this category.</div>
              ) : (
                <div className={ssrStyles.itemList}>
                  {filteredItems.map((item) => {
                    const existing = findExisting(item.id);
                    return (
                      <SSRItemRow
                        key={item.id} item={item}
                        isSelected={!!existing} existing={existing}
                        onToggle={toggleItem} onQtyChange={updateQty}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className={ssrStyles.sectionHeading}>
                Browse by Category
                {(loadingCategories || loadingItems) && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: "var(--text-tertiary)" }}>Loading…</span>
                )}
              </div>
              <div className={ssrStyles.catGrid}>
                {categoryList.map((cat) => (
                  <CategoryCard
                    key={cat.code} cat={cat}
                    count={countByCategory[cat.code] || 0}
                    onClick={() => setActiveCategory(cat.code)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Selected Services Summary ── */}
      {ssrItems.length > 0 && (
        <div>
          <div className={ssrStyles.sectionHeading} style={{ marginTop: 4 }}>
            Selected Services
            <span className={ssrStyles.selectedCount}>{ssrItems.length}</span>
          </div>
          <div className={ssrStyles.selectedList}>
            {ssrItems.map((s, idx) => {
              const cat = findCatLocal(s.category);
              const flightLabel = s.flightProductId ? getFlightLabel(s.flightProductId) : null;

              return (
                <div key={idx} className={ssrStyles.selectedRow}>
                  <span className={ssrStyles.selCatIcon} title={cat?.name}>{cat?.icon || "📋"}</span>

                  <div className={ssrStyles.selCodeBadge} style={{ background: cat?.bg, color: cat?.color }}>
                    {s.code}
                  </div>

                  <div className={ssrStyles.selBody}>
                    <span className={ssrStyles.selLabel}>{s.label}</span>
                    {s.travelerId && (
                      <span className={ssrStyles.selTravelerBadge}>
                        <UserIcon /> {s.travelerLabel || getTravelerLabel(parseInt(s.travelerId))}
                      </span>
                    )}
                    {flightLabel && (
                      <span className={ssrStyles.selFlightBadge}>✈ {flightLabel}</span>
                    )}
                    {s.legId && s.legId !== "__all__" && (
                      <span className={ssrStyles.selLegBadge}>
                        <RouteIcon /> {s.legLabel || s.legId}
                      </span>
                    )}
                    {s.isCustom && (
                      <span className={ssrStyles.selCustomBadge}>Custom</span>
                    )}
                  </div>

                  <div className={ssrStyles.selQtyUnit}>
                    ×{s.qty} <span>{s.unit}</span>
                  </div>

                  {(() => {
                    const c = ssrStatusColor(s.status || "Requested");
                    return (
                      <select
                        value={s.status || "Requested"}
                        onChange={(e) => updateStatus(idx, e.target.value)}
                        title="SSR status"
                        style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 6, fontSize: 11, fontWeight: 600, padding: "3px 6px", cursor: "pointer", outline: "none" }}
                      >
                        {SSR_STATUS_VALUES.map((v) => (
                          <option key={v} value={v} style={{ background: "#fff", color: "#111827" }}>{v}</option>
                        ))}
                      </select>
                    );
                  })()}

                  <button className={ssrStyles.selRemove} onClick={() => removeItem(idx)} title="Remove">
                    <XIcon />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Empty state ── */}
      {ssrItems.length === 0 && !searchQuery && !activeCategory && contextReady && (
        <div className={styles.infoBox} style={{ textAlign: "center", padding: "16px 20px" }}>
          <strong>SSR step is optional.</strong> Select any special services above or continue to the next step. Pricing for SSRs is managed in the Pricing step.
        </div>
      )}

      {/* ── Custom SSR Modal ── */}
      {showAddCustom && (
        <AddCustomSSRModal
          onClose={() => setShowAddCustom(false)}
          onAdd={addCustom}
          categories={categoryList}
        />
      )}
    </div>
  );
};

export default StepSSR;